// Scheduled exports page.
//
// Standalone home for automated delivery, split out of the export builder. You
// pick a template (provided or saved) to deliver, then set the cadence (capped
// at once a month) and recipients via ExportSchedulePanel. Existing schedules
// are listed for pause/remove. Decoupling from a live build means a schedule
// always references a named template, which is also what a server-side cron
// would resolve at send time.
//
// Postgres mapping: see exportData.js `user_exports_schedule`; the chosen
// template id is stored alongside the resolved title/format so the cron can
// re-resolve current data at each send.
import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Snackbar } from '@mui/material';
import MainCard from '../../../components/cards/MainCard';
import useFeature from '../useFeature';
import {
    PROVIDED_TEMPLATES, loadCustomTemplates, SECTION_INDEX,
    loadSchedules, saveSchedule, toggleSchedule, deleteSchedule,
} from '../exportData';
import ExportSchedulePanel from './ExportSchedulePanel';

export default function ScheduledExportsPage() {
    const dwmOn = useFeature('darkWebMonitoring');
    const templates = useMemo(() => [...PROVIDED_TEMPLATES, ...loadCustomTemplates()], []);
    const [templateId, setTemplateId] = useState(templates[0]?.id);
    const [schedules, setSchedules] = useState(() => loadSchedules());
    const [toast, setToast] = useState('');

    const selected = templates.find((t) => t.id === templateId) || templates[0];
    // Count only sections the tenancy can actually render (DWM off hides those).
    const sectionCount = selected
        ? selected.sections.filter((id) => {
            const m = SECTION_INDEX[id];
            return m && !(m.feature === 'darkWebMonitoring' && !dwmOn);
        }).length
        : 0;
    const title = selected ? (selected.title || selected.name) : 'Export';
    const format = selected?.format || 'PDF';

    const onSave = (entry) => {
        setSchedules(saveSchedule({ ...entry, templateId: selected?.id }));
        setToast(`Scheduled ${entry.cadence} delivery to ${entry.recipients.length} recipient${entry.recipients.length === 1 ? '' : 's'}`);
    };
    const onToggle = (id) => setSchedules(toggleSchedule(id));
    const onDelete = (id) => { setSchedules(deleteSchedule(id)); setToast('Schedule removed'); };

    return (
        <Box sx={{ p: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>Scheduled exports</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                    Pick a template and have it delivered automatically. Exports go out a maximum of once a month.
                </Typography>
            </Box>

            <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>What to send</Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Choose the template this schedule delivers.</Typography>
                </Box>
                <Box sx={{ p: 2, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {templates.map((t) => {
                        const active = t.id === templateId;
                        return (
                            <Button
                                key={t.id} size="small" onClick={() => setTemplateId(t.id)}
                                sx={{
                                    textTransform: 'none', fontSize: 11.5, px: 1.5, py: '5px', borderRadius: '6px',
                                    border: '1px solid', borderColor: active ? 'secondary.main' : 'divider',
                                    bgcolor: active ? 'action.selected' : 'transparent',
                                    color: active ? 'secondary.main' : 'text.secondary',
                                    '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' },
                                }}
                            >
                                {t.name}{t.custom ? ' (saved)' : ''}
                            </Button>
                        );
                    })}
                </Box>
                {selected && (
                    <Box sx={{ px: 3, pb: 2 }}>
                        <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>
                            Sends <strong>{title}</strong> · {format} · {sectionCount} section{sectionCount === 1 ? '' : 's'}
                        </Typography>
                    </Box>
                )}
            </MainCard>

            <ExportSchedulePanel
                title={title} format={format} sectionCount={sectionCount}
                schedules={schedules} onSave={onSave} onToggle={onToggle} onDelete={onDelete}
            />

            <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}
